import {
  UseInfiniteQueryOptions,
  UseQueryOptions,
  useInfiniteQuery,
  useMutation,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query';
import { FormikHelpers } from 'formik';
import { get } from 'lodash';
import { SetOptional } from 'type-fest';
import { api } from '@/modules/common/api/index.ts';
import { useGetCurrentBusinessId } from '@/modules/common/hooks/useGetCurrentBusinessId.ts';
import {
  CreateCustomChartRequest,
  CustomChartCalculationsResponse,
  CustomChartCalculationsV1CustomChartsCalculationsGetParams,
  GetCustomChartListResponse,
  GetCustomChartListV1CustomChartsGetParams,
  GetFormFieldsResponse,
  GetFormFieldsV1FormsFieldsByTaskOrIncidentGetParams,
  UpdateCustomChartRequest,
} from 'compliance-api';

export const CUSTOM_CHARTS_LIST_QUERY_KEY = 'vestra-compliance/custom-charts-list';
export const CUSTOM_CHARTS_DETAIL_QUERY_KEY = 'vestra-compliance/custom-charts-detail';
export const CUSTOM_CHARTS_DATA_QUERY_KEY = 'vestra-compliance/custom-charts-data';
export const CUSTOM_CHARTS_FORM_FIELDS_QUERY_KEY = 'vestra-compliance/custom-charts-form-fields';

export const useCustomChartsQuery = (
  {
    page = 1,
    page_size = 10,
    ...rest
  }: SetOptional<GetCustomChartListV1CustomChartsGetParams, 'page' | 'page_size'>,
  options: Partial<UseInfiniteQueryOptions<GetCustomChartListResponse>> = {},
) => {
  const businessId = useGetCurrentBusinessId();

  return useInfiniteQuery<GetCustomChartListResponse>({
    queryKey: [CUSTOM_CHARTS_LIST_QUERY_KEY, businessId, rest],
    staleTime: 1000 * 60 * 5, // 5 minutes
    queryFn: async ({ pageParam = page }) => {
      const response = await api.v1.getCustomChartListV1CustomChartsGet({
        page: pageParam,
        page_size,
        ...rest,
      });
      return response.data;
    },
    getNextPageParam: ({ total, page, page_size }) => {
      const totalPages = Math.ceil(total / page_size);
      if (page < totalPages) {
        return page + 1;
      }
      return undefined;
    },
    ...options,
  });
};

export const useCreateCustomChartMutation = (
  setFormikErrors?: FormikHelpers<unknown>['setErrors'],
) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: CreateCustomChartRequest) => {
      const response = await api.v1.createCustomChartV1CustomChartsPost(data);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [CUSTOM_CHARTS_LIST_QUERY_KEY] });
    },
    onError: e => {
      const errors = get(e, 'response.data.errors', {});
      setFormikErrors?.(errors);
    },
  });
};

export const useUpdateCustomChartMutation = (
  setFormikErrors?: FormikHelpers<unknown>['setErrors'],
) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (payload: UpdateCustomChartRequest & { id: number }) => {
      const response = await api.v1.updateCustomChartV1CustomChartsPatch(payload);
      return response.data;
    },
    onSuccess: (_, { id }) => {
      if (id) {
        queryClient.invalidateQueries({
          queryKey: [CUSTOM_CHARTS_LIST_QUERY_KEY],
        });
        queryClient.invalidateQueries({
          queryKey: [CUSTOM_CHARTS_DETAIL_QUERY_KEY, id],
        });
        queryClient.invalidateQueries({
          queryKey: [CUSTOM_CHARTS_DATA_QUERY_KEY, id],
        });
      }
    },
    onError: e => {
      const errors = get(e, 'response.data.errors', {});
      setFormikErrors?.(errors);
    },
  });
};

export const useDeleteCustomChartMutation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (id: number) => {
      const response = await api.v1.removeCustomChartV1CustomChartsIdDelete({ id });
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [CUSTOM_CHARTS_LIST_QUERY_KEY] });
    },
  });
};

export const useGetCustomChartCalculationsQuery = (
  params: CustomChartCalculationsV1CustomChartsCalculationsGetParams,
  options: Partial<
    UseQueryOptions<CustomChartCalculationsResponse, unknown, CustomChartCalculationsResponse>
  > = {},
) =>
  useQuery<CustomChartCalculationsResponse>({
    queryKey: [CUSTOM_CHARTS_DATA_QUERY_KEY, params.chart_id, params.from_date, params.to_date],
    staleTime: 1000 * 60 * 5, // 5 minutes
    queryFn: async () => {
      const response = await api.v1.customChartCalculationsV1CustomChartsCalculationsGet(params);
      return response.data;
    },
    ...options,
  });

export const useFormFieldsQuery = (
  params: GetFormFieldsV1FormsFieldsByTaskOrIncidentGetParams,
  options: Partial<UseQueryOptions<GetFormFieldsResponse, unknown, GetFormFieldsResponse>> = {},
) =>
  useQuery<GetFormFieldsResponse>({
    queryKey: [
      CUSTOM_CHARTS_FORM_FIELDS_QUERY_KEY,
      { taskId: params.task_id, incidentTypeId: params.incident_type_id },
    ],
    staleTime: 1000 * 60 * 60, // 1 hour
    queryFn: async () => {
      const response = await api.v1.getFormFieldsV1FormsFieldsByTaskOrIncidentGet(params);
      return response.data;
    },
    ...options,
  });
